import { computed, reactive } from '@vue/composition-api';
import type { User } from '@getaccept/lib-shared/src/users/types/user';
import type { Recipient } from '@getaccept/lib-shared/src/types/Recipient';
import type { Chat, ChatParticipant, Message } from '../types';
import { ConversationType } from '../types';
import { ChatHelper } from '../helpers/chat.helper';

export function useChat() {
  const chat: Chat = reactive({
    conversation: undefined,
    conversations: [],
    typing: [],
    error: false,
    loadingConversationList: true,
    loadingConversationDetails: true,
    replyMessage: undefined,
  });

  const setDefaultConversation = () => {
    chat.conversation = chat.conversations.find(({ type }) => type === ConversationType.General);
  };

  const addMessage = (newMessage: Message, id: string, replace?: boolean) => {
    const messageExists = !!chat.conversation.messages.find(message => message.id === id);
    const newMessages = chat.conversation.messages.map(message => {
      if (message.id === id && replace) {
        return newMessage;
      }

      return message;
    });

    chat.conversation = {
      ...chat.conversation,
      messages: messageExists ? newMessages : [...newMessages, newMessage],
    };
  };

  const setReplyMessage = (message: Message) => {
    chat.replyMessage = message;
  };

  const mapChatParticipant = (
    participant: ChatParticipant,
    recipients: Recipient[],
    user: User
  ) => {
    if (participant.recipientId) {
      return {
        ...participant,
        recipient: recipients.find(recipient => recipient.id === participant.recipientId),
      };
    }

    return {
      ...participant,
      user,
    };
  };

  const findLastRead = (messages: Message[], recipientId?: string, userId?: string) => {
    for (const message of [...messages].reverse()) {
      const readBy = message.reads.find(({ participant }) => {
        if (recipientId && participant.recipientId) {
          return recipientId === participant.recipientId;
        }

        return userId === participant.userId;
      });

      if (readBy) {
        return {
          messageId: message.id,
          readBy,
        };
      }
    }
  };

  const findLastReadList = (recipients: Recipient[], user: User) => {
    if (chat.conversation.type === ConversationType.Private) {
      return chat.conversation.participants.map(({ recipientId, userId }) =>
        findLastRead(chat.conversation.messages, recipientId, userId)
      );
    }

    const recipientLastReadBy = recipients.map(recipient =>
      findLastRead(chat.conversation.messages, recipient.id)
    );

    const userLastReadBy = findLastRead(chat.conversation.messages, undefined, user.id);
    return [...recipientLastReadBy, userLastReadBy];
  };

  const useConversationLastReadList = (
    recipients: Recipient[],
    user: User,
    owner: ChatParticipant
  ) =>
    computed(() => {
      const reads = findLastReadList(recipients, user).filter(read => !!read);

      return reads
        .filter(({ readBy: { participant } }) => !ChatHelper.isSameParticipant(participant, owner))
        .map(({ readBy, messageId }) => ({
          messageId,
          readBy: {
            participant: mapChatParticipant(readBy.participant, recipients, user),
            timestamp: readBy.timestamp,
          },
        }));
    });

  return {
    chat,
    setDefaultConversation,
    addMessage,
    mapChatParticipant,
    useConversationLastReadList,
    setReplyMessage,
  };
}
